// (c) chaehongee. Confidential and proprietary.

import { memo, useRef, type SyntheticEvent } from 'react';
import { Handle, Position, useUpdateNodeInternals } from '@xyflow/react';
import { useAppSelector } from '../../store/hooks/hooks';
import { selectSelectedNodeIdsBySessionId } from '../../store/selectors/selectionSelectors';
import { TREE_LAYOUT_CONSTANTS } from './constants';

interface ReactFlowNodeProps {
    id: string;
    data: {
        image_path: string;
        type?: string;
        label?: string;
    };
}

export const ReactFlowNode = memo(({ id, data }: ReactFlowNodeProps) => {
    const mediaUrl = data.image_path;
    const isVideo = data.type === 'video';
    const updateNodeInternals = useUpdateNodeInternals();
    const hasLoadedRef = useRef(false);

    const isSelected = useAppSelector((s) => {
        const sessionId = s.sessionTabs.activeSessionId;
        return sessionId ? selectSelectedNodeIdsBySessionId(s, sessionId).includes(id) : false;
    });

    const circleDiameter = TREE_LAYOUT_CONSTANTS.NODE_SIZE;

    const handleImageLoad = (_e: SyntheticEvent<HTMLImageElement>) => {
        if (!hasLoadedRef.current) {
            hasLoadedRef.current = true;
            updateNodeInternals(id);
        }
    };

    const handleVideoMetadataLoad = (e: SyntheticEvent<HTMLVideoElement>) => {
        const video = e.currentTarget;
        if (!video.videoWidth || !video.videoHeight) {
            return;
        }
        if (!hasLoadedRef.current) {
            hasLoadedRef.current = true;
            updateNodeInternals(id);
        }
    };

    const handleMediaError = (e: SyntheticEvent<HTMLImageElement | HTMLVideoElement>) => {
        // Fallback to colored rectangle
        e.currentTarget.style.display = 'none';
    };

    return (
        <div
            className={`react-flow-node ${isSelected ? 'selected' : ''}`}
            style={{
                position: 'relative',
                width: circleDiameter,
                height: circleDiameter,
                borderRadius: '50%',
                overflow: 'hidden',
                border: isSelected
                    ? '2px solid var(--tree-node-selected-border)'
                    : '0.5px solid var(--tree-node-border)',
                transition: 'all var(--transition)',
                backgroundColor: 'var(--color-surface)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}
        >
            {isVideo ? (
                <video
                    src={mediaUrl}
                    muted
                    playsInline
                    preload="metadata"
                    onLoadedMetadata={handleVideoMetadataLoad}
                    onError={handleMediaError}
                    style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block'
                    }}
                />
            ) : (
                <img
                    src={mediaUrl}
                    alt={`Node ${id}`}
                    onLoad={handleImageLoad}
                    onError={handleMediaError}
                    style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block'
                    }}
                />
            )}

            <Handle
                type="target"
                position={Position.Top}
                style={{
                    opacity: 0,
                    pointerEvents: 'none'
                }}
            />
            <Handle
                type="source"
                position={Position.Bottom}
                style={{
                    opacity: 0,
                    pointerEvents: 'none'
                }}
            />
        </div>
    );
});

ReactFlowNode.displayName = 'ReactFlowNode';
